import { useMemo, useState, type ElementType } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/language";
import { fetchWithAuth } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ClipboardList, HelpCircle, CheckCircle2, XCircle, Info, ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface SessionHistoryRow {
  id: number;
  mode: string;
  category: string | null;
  track: string;
  totalQuestions: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  answered: number;
  accuracy: number;
  completedAt: string | null;
  createdAt: string;
}

interface SessionHistoryResponse {
  sessions: SessionHistoryRow[];
}

async function fetchSessionHistory(): Promise<SessionHistoryResponse> {
  const res = await fetchWithAuth<SessionHistoryResponse>("/api/stats/session-history?limit=50");
  return res;
}

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function StatCell({
  icon: Icon,
  value,
  label,
  tone,
}: {
  icon: ElementType;
  value: number;
  label: string;
  tone: "default" | "muted" | "bad" | "good";
}) {
  const iconCls =
    tone === "good" ? "text-green-600"
    : tone === "bad" ? "text-red-500"
    : tone === "muted" ? "text-sky-600"
    : "text-muted-foreground";
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-background/40 px-2 py-2.5 min-w-0">
      <Icon className={cn("w-4 h-4 shrink-0", iconCls)} aria-hidden />
      <span className="text-lg font-bold font-mono tabular-nums leading-none">{value}</span>
      <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground text-center leading-tight">
        {label}
      </span>
    </div>
  );
}

/** Session review list + summary. Use `embedded` inside the flight deck sheet so the main page stays short. */
export function SessionHistoryPanel({ embedded = false }: { embedded?: boolean }) {
  const { t, language } = useLanguage();
  const h = t.sessionHistory;
  const locale = language === "fr" ? "fr-CA" : "en-CA";
  const [shuffleOn, setShuffleOn] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["session-history"],
    queryFn: fetchSessionHistory,
    staleTime: 60_000,
  });

  const displaySessions = useMemo(() => {
    const list = data?.sessions ?? [];
    if (list.length === 0) return [];
    return shuffleOn ? shuffled(list) : list;
  }, [data?.sessions, shuffleOn]);

  const summary = useMemo(() => {
    return displaySessions.reduce(
      (acc, s) => {
        acc.all += s.totalQuestions;
        acc.unanswered += s.unanswered;
        acc.incorrect += s.incorrect;
        acc.correct += s.correct;
        return acc;
      },
      { all: 0, unanswered: 0, incorrect: 0, correct: 0 },
    );
  }, [displaySessions]);

  const modeLabel = (mode: string) => {
    switch (mode) {
      case "random": return h.modeRandom;
      case "category": return h.modeCategory;
      case "weakness": return h.modeWeakness;
      case "exam": return h.modeExam;
      case "scenario": return h.modeScenario;
      default: return mode;
    }
  };

  const Heading = embedded ? "h2" : "h1";
  const titleClass = embedded
    ? "text-lg font-bold tracking-tight text-foreground"
    : "text-2xl sm:text-3xl font-bold tracking-tight text-foreground";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col",
        embedded ? "h-full gap-3" : "mx-auto max-w-3xl space-y-6 animate-in fade-in duration-500",
      )}
    >
      <div className={cn("flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", embedded && "gap-2")}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Heading className={titleClass}>{h.title}</Heading>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="rounded-full p-1 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={h.infoHint}
                >
                  <Info className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 text-xs leading-relaxed font-mono">
                {h.infoHint}
              </PopoverContent>
            </Popover>
          </div>
          <p className={cn("text-muted-foreground mt-1", embedded ? "text-xs leading-snug" : "text-sm")}>{h.subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2">
          <Switch id={embedded ? "shuffle-sessions-deck" : "shuffle-sessions"} checked={shuffleOn} onCheckedChange={setShuffleOn} />
          <Label htmlFor={embedded ? "shuffle-sessions-deck" : "shuffle-sessions"} className="cursor-pointer whitespace-nowrap font-mono text-xs">
            {h.shuffle}
          </Label>
        </div>
      </div>

      <div className={cn(embedded && "min-h-0 flex-1 overflow-y-auto pr-1")}>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-36 w-full rounded-xl" />
            ))}
          </div>
        ) : isError ? (
          <div className="py-8 text-center font-mono text-sm text-destructive">{h.loadError}</div>
        ) : displaySessions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card/30 py-10 text-center">
            <p className="mb-4 font-mono text-sm text-muted-foreground">{h.empty}</p>
            <Link
              href="/practice"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              {h.goPractice}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className={cn("space-y-4", embedded && "space-y-3 pb-4")}>
            <div className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{h.summaryTitle}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCell icon={ClipboardList} value={summary.all} label={h.colAll} tone="default" />
                <StatCell icon={HelpCircle} value={summary.unanswered} label={h.colUnanswered} tone="muted" />
                <StatCell icon={XCircle} value={summary.incorrect} label={h.colIncorrect} tone="bad" />
                <StatCell icon={CheckCircle2} value={summary.correct} label={h.colCorrect} tone="good" />
              </div>
            </div>

            {displaySessions.map((s) => {
              const when = s.completedAt
                ? new Date(s.completedAt).toLocaleString(locale, {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
                : "—";
              const title = [modeLabel(s.mode), s.category || h.allCategories].join(" · ");

              return (
                <article
                  key={s.id}
                  className={cn(
                    "rounded-xl border border-border bg-card shadow-sm",
                    embedded ? "space-y-3 p-3" : "space-y-4 p-4 sm:p-5",
                  )}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="font-bold leading-snug text-foreground">{title}</h3>
                      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {when}
                        <span className="mx-1.5 text-border">|</span>
                        {h.trackLabel}: <span className="uppercase">{s.track}</span>
                        <span className="mx-1.5 text-border">|</span>
                        {s.accuracy.toFixed(0)}% {h.accuracyShort}
                      </p>
                    </div>
                    <Link
                      href={`/review/${s.id}`}
                      className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 font-mono text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      {h.reviewCta}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <StatCell icon={ClipboardList} value={s.totalQuestions} label={h.colAll} tone="default" />
                    <StatCell icon={HelpCircle} value={s.unanswered} label={h.colUnanswered} tone="muted" />
                    <StatCell icon={XCircle} value={s.incorrect} label={h.colIncorrect} tone="bad" />
                    <StatCell icon={CheckCircle2} value={s.correct} label={h.colCorrect} tone="good" />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
